import * as THREE from 'three';
import { Socket } from 'socket.io-client';
import { LabelManager2D } from './LabelManager2D';
import { CityGenerator } from './CityGenerator'; // Import CityGenerator

export class PostmanController {
  private scene: THREE.Scene;
  private socket: Socket;
  private player: THREE.Group;
  private cityGenerator: CityGenerator; // Add reference to CityGenerator
  private speed: number = 25.0; // Unités par seconde au lieu de par frame
  private currentSpeed: number = 0.0; // Vitesse actuelle avec accélération/décélération
  private maxSpeed: number = 25.0; // Vitesse maximale en unités par seconde
  private acceleration: number = 40.0; // Taux d'accélération en unités par seconde²
  private deceleration: number = 80.0; // Taux de décélération en unités par seconde² (plus élevé pour un freinage plus rapide)
  private position: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private direction: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private isMoving: boolean = false;
  private isGridInitialized: boolean = false; // Track if the grid is initialized
  private thief: boolean = false; // Indique si le joueur est considéré comme un voleur
  private confidence: number = 1; // Niveau de confiance du joueur, initialisé à 1
  private missingPackets: string[] = []; // Liste des IDs de colis volés au joueur
  // Référence aux boutons de contrôle mobile
  private mobileButtons: {
    forward: HTMLElement | null;
    backward: HTMLElement | null;
    left: HTMLElement | null;
    right: HTMLElement | null;
    map: HTMLElement | null; // Ajout du bouton map
    sell: HTMLElement | null; // Ajout du bouton vendre pour le marché noir
  };
  private mobileHint: HTMLElement | null;
  private isMobile: boolean;
  // Nouvelle variable pour la rotation progressive
  private targetRotationY: number = 0;
  private rotationSpeed: number = 20.0; // Augmentation de la vitesse de rotation de 3.0 à 6.0
  // Variables pour la vue top-down
  private isTopDownView: boolean = false;
  private onCameraViewChanged: ((isTopDown: boolean) => void) | null = null;
  // Variable pour le timing
  private lastUpdateTime: number = 0;
  // Controls enabled/disabled flag
  private controlsEnabled: boolean = false;
  private labelManager: LabelManager2D | null = null;
  // Variables pour suivre l'état des touches
  private keyStates: { [key: string]: boolean } = {
    'ArrowUp': false,
    'z': false, // Added for AZERTY keyboards
    'ArrowDown': false,
    'ArrowLeft': false,
    'q': false, // Added for AZERTY keyboards
    'ArrowRight': false,
    'd': false
  };
  // Variable pour stocker le type de clavier
  private keyboardLayout: 'qwerty' | 'azerty' = 'qwerty';
  // Variable pour suspendre les rotations
  private rotationCooldown: boolean = false;
  private rotationCooldownTimeout: number | null = null;
  // Variables pour le marché noir
  private isInBlackMarketArea: boolean = false;
  private blackMarketNotification: HTMLElement | null = null;
  private sellProgressBar: HTMLElement | null = null;
  private isSelling: boolean = false;
  private sellStartTime: number = 0;
  private sellDuration: number = 3000; // 3 secondes
  private sellProgressBarContainer: HTMLElement | null = null;
  private sellTimeout: number | null = null;
  // Variables pour la gestion des collisions avec la police
  private isBeingInspected: boolean = false;
  private inspectionTimeout: number | null = null;
  private hasStolenPackages: boolean = false;
  private lastPatrolCollisionTime: number = 0; // Timestamp de la dernière collision avec une patrouille
  private readonly PATROL_COLLISION_COOLDOWN: number = 3000; // 3 secondes de délai
  // Variables pour la police station
  private isInPoliceStationArea: boolean = false;
  private policeStationNotification: HTMLElement | null = null;
  // Variables pour la gestion de la fin de partie
  private gameOverCallback: ((reason: string, score: number) => void) | null = null;
  // Propriétés pour la déclaration de vol
  private isDeclaring: boolean = false;
  private declareStartTime: number = 0;
  private declareDuration: number = 3000; // 3 secondes
  private declareProgressBar: HTMLElement | null = null;
  private declareProgressBarContainer: HTMLElement | null = null;
  private declareTimeout: number | null = null;

  constructor(scene: THREE.Scene, socket: Socket, cityGenerator: CityGenerator) {
    this.scene = scene;
    this.socket = socket;
    this.cityGenerator = cityGenerator; // Store cityGenerator
    
    // Détecter si on est sur mobile
    this.isMobile = this.detectMobile();
    
    // Detect keyboard layout
    this.detectKeyboardLayout();
    
    // Create player mesh in Mini Motorways style
    this.player = this.createVehicle();
    
    this.scene.add(this.player);
    
    // Initialiser la rotation cible avec la rotation actuelle
    this.targetRotationY = this.player.rotation.y;
    
    // Initialiser le temps pour le mouvement basé sur le temps
    this.lastUpdateTime = performance.now();
    
    // Initialiser les références aux boutons mobiles
    this.mobileButtons = {
      forward: document.getElementById('forward-btn'),
      backward: document.getElementById('backward-btn'),
      left: document.getElementById('left-btn'),
      right: document.getElementById('right-btn'),
      map: document.getElementById('map-btn'), // Référence au bouton map
      sell: null // Le bouton de vente sera créé dynamiquement
    };
    
    this.mobileHint = document.getElementById('mobile-hint');
    
    // Masquer l'indice mobile après quelques secondes
    if (this.mobileHint && this.isMobile) {
      setTimeout(() => {
        if (this.mobileHint) {
          this.mobileHint.style.opacity = '0';
          setTimeout(() => {
            if (this.mobileHint) {
              this.mobileHint.style.display = 'none';
            }
          }, 1000);
        }
      }, 5000);
    } else if (this.mobileHint && !this.isMobile) {
      // Cacher l'indice sur desktop
      this.mobileHint.style.display = 'none';
    }
    
    // Set up key listeners
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
    document.addEventListener('keyup', this.handleKeyUp.bind(this));
    
    // Configurer les contrôles tactiles pour mobile
    this.setupMobileControls();
    
    // Demander immédiatement la position initiale au serveur
    socket.emit('requestInitialPosition');
    
    // Écouter l'événement d'initialisation du joueur pour définir sa position initiale
    this.socket.on('existingPlayers', (players) => {
      // Trouver ses propres données de joueur
      const myPlayer = players.find((p: any) => p.id === this.socket.id);
      if (myPlayer && myPlayer.position) {
        this.setInitialPosition(myPlayer.position);
      }
    });
    
    // Écouter l'événement initialPosition du serveur
    this.socket.on('initialPosition', (data) => {
      if (data.id === this.socket.id && data.position) {
        console.log("Received initial position from server:", data.position);
        this.setInitialPosition(data.position);
      }
    });
    
    // Écouter l'événement cityGrid pour savoir quand la grille est initialisée
    this.socket.on('cityGrid', () => {
      setTimeout(() => {
        // Attendre un peu que la grille soit complètement chargée
        this.isGridInitialized = true;
        console.log("Grid initialized for movement validation");
      }, 500);
    });
    
    // Créer les éléments UI pour le marché noir
    this.createBlackMarketUI();
    console.log("Black market UI created");
    
    // Écouter l'événement de collision avec un patrouilleur
    this.socket.on('patrolCollision', (data) => {
      this.handlePatrolCollision(data);
    });
    
    // Écouter l'événement de fin de partie
    this.socket.on('gameOver', (data) => {
      this.handleGameOver(data);
    });

    // Créer l'interface utilisateur de la déclaration de vol
    this.createDeclareUI();
    
    // Écouter les mises à jour de packages (après collision, par exemple)
    this.socket.on('packageUpdate', (data) => {
      if (data.id === this.socket.id) {
        // Mise à jour des informations du joueur
        if (data.thief !== undefined) {
          this.thief = data.thief;
        }
        
        if (data.confidence !== undefined) {
          this.confidence = data.confidence;
        }
        
        // Mise à jour de la liste des colis manquants
        if (data.missingPackets !== undefined) {
          this.missingPackets = data.missingPackets;
          console.log("Mise à jour de la liste des colis manquants:", this.missingPackets);
          
          // Afficher une notification si des colis ont été volés
          if (this.missingPackets.length > 0) {
            this.showNotification('Warning! Some of your packages have been stolen!', '#ff5733', 3000);
          }
        }
      }
    });
  }
  
  // Vérifie si une position est valide pour le déplacement (route, pont ou marché noir)
  private isValidPosition(position: THREE.Vector3): boolean {
    // Si la grille n'est pas encore initialisée, autoriser le mouvement
    if (!this.isGridInitialized) {
      return true;
    }
    
    // Récupérer les indices de la grille pour cette position
    const indices = this.cityGenerator.calculateIndex(position);
    
    // Récupérer les données de la grille
    const cityGrid = this.cityGenerator.getCityGrid();
    if (!cityGrid || !cityGrid.blocks || !cityGrid.blocks[indices.i]) {
      return true; // Si les données de la grille ne sont pas disponibles, autoriser le mouvement
    }
    
    // Vérifier si la position est dans les limites de la grille
    if (indices.i < 0 || indices.i >= cityGrid.blocks.length || 
        indices.j < 0 || indices.j >= cityGrid.blocks[0].length) {
      // console.log(`Position hors grille: [${indices.i}, ${indices.j}], position: (${position.x.toFixed(2)}, ${position.z.toFixed(2)})`);
      return false;
    }
    
    const cellType = cityGrid.blocks[indices.i][indices.j];
    // console.log(`Position: (${position.x.toFixed(2)}, ${position.z.toFixed(2)}), indices: [${indices.i}, ${indices.j}], cellType: ${cellType}`);
    
    // Valeurs acceptables pour le déplacement:
    // 5 = route, 10/11 = routes, 6/7 = ponts, 40 = route, 12 = marché noir, 21 = route
    const isValid = cellType === 5 || cellType === 10 || 
                   cellType === 6 || cellType === 7 || cellType === 40 || cellType === 12 || cellType === 21;
    
    // Si c'est une case de marché noir, afficher un message de débogage
    if (cellType === 12) {
      console.log("Position sur une case de marché noir (type 12)");
    }
    
    return isValid;
  }
  
  // Calculer la position future basée sur le mouvement et vérifier si elle est valide
  private predictNextPosition(moveVector: THREE.Vector3, deltaTime: number): THREE.Vector3 {
    const nextPosition = this.position.clone();
    nextPosition.add(moveVector.clone().multiplyScalar(this.currentSpeed * deltaTime));
    return nextPosition;
  }
  
  // Pour vérifier si une rotation à droite/gauche est valide
  private isValidRotation(targetRotation: number): boolean {
    // Si la grille n'est pas encore initialisée, autoriser la rotation
    if (!this.isGridInitialized) {
      return true;
    }
    
    // Si le véhicule est à l'arrêt, autoriser les rotations dans tous les cas
    // Cela permet au joueur de faire demi-tour même s'il est face à un obstacle
    if (!this.isMoving) {
      return true;
    }
    
    // Normaliser les rotations pour la comparaison
    const normalizedCurrent = (this.player.rotation.y + 2 * Math.PI) % (2 * Math.PI);
    const normalizedTarget = (targetRotation + 2 * Math.PI) % (2 * Math.PI);
    
    // Calculer la différence de rotation (sens horaire)
    const rotationDiff = (normalizedTarget - normalizedCurrent + 2 * Math.PI) % (2 * Math.PI);
    
    // Récupérer directement les indices de la grille pour la position actuelle
    const indices = this.cityGenerator.calculateIndex(this.position);
    const cityGrid = this.cityGenerator.getCityGrid();
    
    if (!cityGrid || !cityGrid.blocks) {
      // Si on n'a pas accès à la grille, autoriser par défaut
      return true;
    }
    
    // Déterminer si on tourne à gauche ou à droite
    // et vérifier la case adjacente directement dans la grille
    if (Math.abs(rotationDiff - Math.PI/2) < 0.1) {
      // Tourne à gauche
      console.log("Checking left turn");
      
      // Définir les indices à vérifier en fonction de la rotation actuelle
      let checkI = indices.i;
      let checkJ = indices.j;
      
      // On considère les 4 directions cardinales
      if (Math.abs(normalizedCurrent) < 0.1 || Math.abs(normalizedCurrent - 2*Math.PI) < 0.1) {
        // Face au Nord (Z+), vérifier côté gauche (X+)
        checkI = indices.i + 1;
      } else if (Math.abs(normalizedCurrent - Math.PI/2) < 0.1) {
        // Face à l'Est (X+), vérifier côté gauche (Z-)
        checkJ = indices.j - 1;
      } else if (Math.abs(normalizedCurrent - Math.PI) < 0.1) {
        // Face au Sud (Z-), vérifier côté gauche (X-)
        checkI = indices.i - 1;
      } else if (Math.abs(normalizedCurrent - 3*Math.PI/2) < 0.1) {
        // Face à l'Ouest (X-), vérifier côté gauche (Z+)
        checkJ = indices.j + 1;
      }

      // Vérifier que les indices sont dans la grille
      if (checkI < 0 || checkI >= cityGrid.blocks.length || 
          checkJ < 0 || checkJ >= cityGrid.blocks[0].length) {
        console.log("Indices hors grille:", checkI, checkJ);
        return false;
      }
      
      // Vérifier si les indices sont valides et si la case est accessible
      if (checkI >= 0 && checkI < cityGrid.blocks.length && 
          checkJ >= 0 && checkJ < cityGrid.blocks[0].length) {
        const cellType = cityGrid.blocks[checkI][checkJ];
        
        // Valider si la case est une route, un pont ou le marché noir
        const isValid = cellType === 5 || cellType === 10 || cellType === 11 ||
                       cellType === 6 || cellType === 7 || cellType === 40 || cellType === 12 || cellType === 21;
        
        // Si c'est une case de marché noir, afficher un message de débogage
        if (cellType === 12) {
          console.log("Rotation vers une case de marché noir (type 12)");
        }
        
        return isValid;
      }
    } 
    else if (Math.abs(rotationDiff - 3*Math.PI/2) < 0.1) {
      // Tourne à droite
      console.log("Checking right turn");
      // Définir les indices à vérifier en fonction de la rotation actuelle
      let checkI = indices.i;
      let checkJ = indices.j;
      
      // On considère les 4 directions cardinales
      if (Math.abs(normalizedCurrent) < 0.1 || Math.abs(normalizedCurrent - 2*Math.PI) < 0.1) {
        // Face au Nord (Z+), vérifier côté droit (X-)
        checkI = indices.i - 1;
      } else if (Math.abs(normalizedCurrent - Math.PI/2) < 0.1) {
        // Face à l'Est (X+), vérifier côté droit (Z+)
        checkJ = indices.j + 1;
      } else if (Math.abs(normalizedCurrent - Math.PI) < 0.1) {
        // Face au Sud (Z-), vérifier côté droit (X+)
        checkI = indices.i + 1;
      } else if (Math.abs(normalizedCurrent - 3*Math.PI/2) < 0.1) {
        // Face à l'Ouest (X-), vérifier côté droit (Z-)
        checkJ = indices.j - 1;
      }
      
      // Vérifier si les indices sont valides et si la case est accessible
      if (checkI >= 0 && checkI < cityGrid.blocks.length && 
          checkJ >= 0 && checkJ < cityGrid.blocks[0].length) {
        const cellType = cityGrid.blocks[checkI][checkJ];
        // console.log("cellType", cellType);
        // Valider si la case est une route, un pont ou le marché noir
        const isValid = cellType === 5 || cellType === 10 || cellType === 11 ||
                      cellType === 6 || cellType === 7 || cellType === 40 || cellType === 12 || cellType === 21;
        
        // Si c'est une case de marché noir, afficher un message de débogage
        if (cellType === 12) {
          console.log("Rotation vers une case de marché noir (type 12)");
        }
        
        return isValid;
      }
    }
    // Pour les autres rotations (demi-tour ou cas non gérés)
    return true;
  }
  
  // Détecter si on est sur un appareil mobile
  private detectMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           window.innerWidth <= 768;
  }
  
  private setupMobileControls(): void {
    // Vérifier si les boutons existent
    if (!this.mobileButtons.forward || 
        !this.mobileButtons.backward || 
        !this.mobileButtons.left || 
        !this.mobileButtons.right) {
      console.warn('Mobile control buttons not found in DOM');
      return;
    }
    
    // Gestion des événements tactiles pour les boutons de direction
    
    // Bouton Avancer
    this.mobileButtons.forward.addEventListener('touchstart', (e) => {
      e.preventDefault(); // Empêcher le défilement de la page
      if (!this.controlsEnabled) return;
      
      this.isMoving = true;
      this.direction.z = 1;
      this.keyStates['ArrowUp'] = true; // Mettre à jour l'état de la touche virtuelle
      this.resetToThirdPersonView(); // Retourner à la vue 3ème personne sur mouvement
    });
    
    this.mobileButtons.forward.addEventListener('touchend', () => {
      this.direction.z = 0;
      this.keyStates['ArrowUp'] = false; // Mettre à jour l'état de la touche virtuelle
      if (this.direction.length() === 0) {
        this.isMoving = false;
      }
    });
    
    // Bouton Reculer
    this.mobileButtons.backward.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (!this.controlsEnabled) return;
      this.isMoving = true;
      this.direction.z = -1;
      this.keyStates['ArrowDown'] = true; // Mettre à jour l'état de la touche virtuelle
      this.resetToThirdPersonView(); // Retourner à la vue 3ème personne sur mouvement
    });
    
    this.mobileButtons.backward.addEventListener('touchend', () => {
      this.direction.z = 0;
      this.keyStates['ArrowDown'] = false; // Mettre à jour l'état de la touche virtuelle
      if (this.direction.length() === 0) {
        this.isMoving = false;
      }
    });
    
    // Bouton Gauche (tourner)
    this.mobileButtons.left.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (!this.controlsEnabled) return;
      this.keyStates['ArrowLeft'] = true; // Mettre à jour l'état de la touche virtuelle
      this.resetToThirdPersonView(); // Retourner à la vue 3ème personne sur mouvement
    });
    
    this.mobileButtons.left.addEventListener('touchend', () => {
      this.keyStates['ArrowLeft'] = false; // Mettre à jour l'état de la touche virtuelle
    });
    
    // Bouton Droite (tourner)
    this.mobileButtons.right.addEventListener('touchstart', (e) => {
      e.preventDefault();
      // Définir la rotation cible au lieu de tourner instantanément
      if (!this.controlsEnabled) return;
      this.keyStates['ArrowRight'] = true; // Mettre à jour l'état de la touche virtuelle
      this.resetToThirdPersonView(); // Retourner à la vue 3ème personne sur mouvement
    });
    
    this.mobileButtons.right.addEventListener('touchend', () => {
      this.keyStates['ArrowRight'] = false; // Mettre à jour l'état de la touche virtuelle
    });
    
    // Bouton Map (si existant)
    if (this.mobileButtons.map) {
      this.mobileButtons.map.addEventListener('touchstart', (e) => {
        e.preventDefault();
        if (!this.controlsEnabled) return;
        this.toggleTopDownView();
      });
      
      this.mobileButtons.map.addEventListener('click', () => {
        if (!this.controlsEnabled) return;
        this.toggleTopDownView();
      });
    }
    
    // Ajouter aussi des gestionnaires pour les clics normaux (pour tester sur desktop)
    this.mobileButtons.forward.addEventListener('mousedown', () => {
      if (!this.controlsEnabled) return;
      this.isMoving = true;
      this.direction.z = 1;
      this.keyStates['ArrowUp'] = true; // Mettre à jour l'état de la touche virtuelle
      this.resetToThirdPersonView(); // Retourner à la vue 3ème personne sur mouvement
    });
    
    this.mobileButtons.forward.addEventListener('mouseup', () => {
      if (!this.controlsEnabled) return;
      this.direction.z = 0;
      this.keyStates['ArrowUp'] = false; // Mettre à jour l'état de la touche virtuelle
      if (this.direction.length() === 0) {
        this.isMoving = false;
      }
    });
    
    this.mobileButtons.backward.addEventListener('mousedown', () => {
      if (!this.controlsEnabled) return;
      this.isMoving = true;
      this.direction.z = -1;
      this.keyStates['ArrowDown'] = true; // Mettre à jour l'état de la touche virtuelle
      this.resetToThirdPersonView(); // Retourner à la vue 3ème personne sur mouvement
    });
    
    this.mobileButtons.backward.addEventListener('mouseup', () => {
      if (!this.controlsEnabled) return;
      this.direction.z = 0;
      this.keyStates['ArrowDown'] = false; // Mettre à jour l'état de la touche virtuelle
      if (this.direction.length() === 0) {
        this.isMoving = false;
      }
    });
    
    this.mobileButtons.left.addEventListener('mousedown', () => {
      if (!this.controlsEnabled) return;
      this.keyStates['ArrowLeft'] = true; // Mettre à jour l'état de la touche virtuelle
      this.resetToThirdPersonView(); // Retourner à la vue 3ème personne sur mouvement
    });
    
    this.mobileButtons.left.addEventListener('mouseup', () => {
      this.keyStates['ArrowLeft'] = false; // Mettre à jour l'état de la touche virtuelle
    });
    
    this.mobileButtons.right.addEventListener('mousedown', () => {
      this.keyStates['ArrowRight'] = true; // Mettre à jour l'état de la touche virtuelle
      this.resetToThirdPersonView(); // Retourner à la vue 3ème personne sur mouvement
    });
    
    this.mobileButtons.right.addEventListener('mouseup', () => {
      this.keyStates['ArrowRight'] = false; // Mettre à jour l'état de la touche virtuelle
    });
  }
  
  private createVehicle(): THREE.Group {
    const vehicle = new THREE.Group();
    // Load the FBX model for the car
    const assetPath = 'cartooncity_141_fbx/Cars/pickupwheels1.fbx';
    // const assetPath = 'cartooncity_141_fbx/Cars/City_Car_16.fbx';
    const position = new THREE.Vector3(0, -0.27, 0); // Ajusté pour correspondre à la position des autres joueurs
    const rotation = new THREE.Euler(-Math.PI/2, 0, 0); // Orientation normale
    const scale = new THREE.Vector3(0.00335, 0.00335, 0.00335); // Reduce scale by 33% (from 0.005 to 0.00335)
    
    console.log("Chargement du modèle de voiture:", assetPath);
    
    // Utiliser loadModelAsset avec null pour la couleur personnalisée afin d'utiliser la texture commune
    this.cityGenerator.loadModelAsset(assetPath, position, rotation, scale, vehicle, null);
    
    return vehicle;
  }

  public getPosition(): THREE.Vector3 {
    return this.position.clone();
  }

  public getPlayer(): THREE.Group {
    return this.player;
  }

  // Appelé dans la méthode update pour vérifier si le joueur est dans une zone spéciale
  public update(): void {
    // Skip updates if controls are disabled
    if (!this.controlsEnabled) return;
    
    // Calcul du delta time pour un mouvement cohérent entre les différents framerates
    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastUpdateTime) / 1000; // Convertir en secondes
    this.lastUpdateTime = currentTime;
    
    // Limiter deltaTime pour éviter les grands sauts après pause/freeze
    const cappedDeltaTime = Math.min(deltaTime, 0.1);
    
    // Rotation progressive vers la cible, basée sur le temps
    if (this.player.rotation.y !== this.targetRotationY) {
      // Calculer le chemin le plus court pour tourner (sens horaire ou anti-horaire)
      let delta = this.targetRotationY - this.player.rotation.y;
      
      // S'assurer que delta est dans l'intervalle [-PI, PI] pour la rotation la plus courte
      if (delta > Math.PI) delta -= 2 * Math.PI;
      if (delta < -Math.PI) delta += 2 * Math.PI;
      
      // Appliquer une rotation progressive basée sur le temps
      const rotationAmount = this.rotationSpeed * cappedDeltaTime;
      
      if (Math.abs(delta) < rotationAmount) {
        // Si on est presque arrivé, aller directement à la cible
        this.player.rotation.y = this.targetRotationY;
      } else {
        // Sinon, effectuer une rotation partielle basée sur le temps
        this.player.rotation.y += Math.sign(delta) * rotationAmount;
        
        // Normaliser l'angle entre 0 et 2*PI
        if (this.player.rotation.y < 0) this.player.rotation.y += 2 * Math.PI;
        if (this.player.rotation.y >= 2 * Math.PI) this.player.rotation.y -= 2 * Math.PI;
      }
    }
    
    // Vérifier les touches directionnelles pour les virages
    this.checkDirectionKeys();
    
    // Gestion de l'accélération et décélération
    if (this.isMoving) {
      // Accélération progressive
      this.currentSpeed = Math.min(this.maxSpeed, this.currentSpeed + (this.acceleration * cappedDeltaTime));
    } else {
      // Décélération rapide lorsqu'aucune touche n'est enfoncée
      this.currentSpeed = Math.max(0, this.currentSpeed - (this.deceleration * cappedDeltaTime));
    }
    
    // Update player position based on key input, avec deltaTime
    if (this.currentSpeed > 0) { // Modifier la condition pour se déplacer si la vitesse actuelle est > 0
      // Créer un vecteur de mouvement basé sur la direction
      const moveVector = new THREE.Vector3();
      
      // Appliquer la rotation du joueur au vecteur de direction
      const rotationMatrix = new THREE.Matrix4();
      rotationMatrix.makeRotationY(this.player.rotation.y);
      
      // Obtenir la direction alignée avec la rotation du joueur
      moveVector.copy(this.direction);
      moveVector.applyMatrix4(rotationMatrix);
      
      // Prédire la position future et vérifier si elle est valide
      let nextPosition = this.predictNextPosition(moveVector, cappedDeltaTime);
      
      // Double vérification: récupérer les indices de la grille pour cette position
      const indices = this.cityGenerator.calculateIndex(nextPosition);
      const cityGrid = this.cityGenerator.getCityGrid();
      
      // Vérification stricte pour ne pas sortir de la grille
      const isInGrid = cityGrid && cityGrid.blocks && 
                     indices.i >= 0 && indices.i < cityGrid.blocks.length &&
                     indices.j >= 0 && indices.j < cityGrid.blocks[0].length;
      
      if (isInGrid && this.isValidPosition(nextPosition)) {
        // Si la position est valide, mettre à jour la position
        this.position.copy(nextPosition);
        this.player.position.copy(this.position);
        
        // Si on est en train de déclarer un vol et qu'on quitte la zone de police, annuler la déclaration
        if (this.isDeclaring && !this.isInPoliceStationArea) {
          console.log("Déclaration annulée: le joueur a quitté la zone de police");
          this.stopDeclaring();
        }
      } else {
        // Si la position n'est pas valide ou hors grille, arrêter le mouvement et réduire la vitesse à zéro
        console.log("Mouvement arrêté: position non valide ou hors grille");
        this.isMoving = false;
        this.currentSpeed = 0;
      }
    }
    
    // Update the player's name label position if available
    if (this.labelManager && this.socket.id) {
      this.labelManager.updateLabel(this.socket.id, this.position);
    }
    
    // Emit position and rotation to server
    this.socket.emit('updatePosition', {
      position: this.position,
      rotation: this.player.rotation.y,
      speed: this.currentSpeed // Envoyer la vitesse actuelle au lieu de la vitesse maximale
    });
    
    // Check if the player is in the black market area
    this.checkBlackMarketArea();
    
    // Check if the player is in the police station area
    this.checkPoliceStationArea();
  }

  // Nouvelle méthode pour vérifier l'état des touches de direction
  private checkDirectionKeys(): void {
    // Si le cooldown est actif, ne pas traiter les rotations
    if (this.rotationCooldown) {
      return;
    }

    // Movement keys based on keyboard layout
    const forwardKeys = this.keyboardLayout === 'qwerty' ? ['ArrowUp', 'w'] : ['ArrowUp', 'z'];
    const backwardKeys = ['ArrowDown', 's']; // Same for both layouts
    const leftKeys = this.keyboardLayout === 'qwerty' ? ['ArrowLeft', 'a'] : ['ArrowLeft', 'q'];
    const rightKeys = ['ArrowRight', 'd']; // Same for both layouts

    // Check if any forward key is pressed
    if (forwardKeys.some(key => this.keyStates[key])) {
      this.isMoving = true;
      this.direction.z = 1;
    }

    // Check if any backward key is pressed
    if (backwardKeys.some(key => this.keyStates[key])) {
      this.isMoving = true;
      this.direction.z = -1;
    }
    
    // Vérifier si une touche gauche est enfoncée
    if (leftKeys.some(key => this.keyStates[key])) {
      // Sauvegarder l'état de mouvement actuel pour permettre le virage en mouvement
      const wasMoving = this.isMoving;
      const currentDirection = this.direction.clone();
      
      // Calculer la nouvelle rotation cible
      const newTargetRotation = (this.targetRotationY + Math.PI/2) % (2 * Math.PI);
      
      // Vérifier si la rotation est valide
      if (this.isValidRotation(newTargetRotation)) {
        this.targetRotationY = newTargetRotation;
        
        // Restaurer l'état de mouvement - pour maintenir le mouvement pendant la rotation
        this.isMoving = wasMoving;
        this.direction.copy(currentDirection);
        
        // Activer le cooldown de rotation
        this.setRotationCooldown();
      }
    }
    
    // Vérifier si une touche droite est enfoncée
    if (rightKeys.some(key => this.keyStates[key])) {
      // Sauvegarder l'état de mouvement actuel pour permettre le virage en mouvement
      const wasMoving = this.isMoving;
      const currentDirection = this.direction.clone();
      
      // Calculer la nouvelle rotation cible
      let newTargetRotation = (this.targetRotationY - Math.PI/2) % (2 * Math.PI);
      // Gérer les valeurs négatives
      if (newTargetRotation < 0) {
        newTargetRotation += 2 * Math.PI;
      }
      
      // Vérifier si la rotation est valide
      if (this.isValidRotation(newTargetRotation)) {
        this.targetRotationY = newTargetRotation;
        
        // Restaurer l'état de mouvement - pour maintenir le mouvement pendant la rotation
        this.isMoving = wasMoving;
        this.direction.copy(currentDirection);
        
        // Activer le cooldown de rotation
        this.setRotationCooldown();
      }      
    }
  }

  // Nouvelle méthode pour activer le cooldown de rotation
  private setRotationCooldown(): void {
    // Activer le cooldown
    this.rotationCooldown = true;
    console.log("Rotation cooldown activé");
    
    // Nettoyer tout timeout existant
    if (this.rotationCooldownTimeout !== null) {
      window.clearTimeout(this.rotationCooldownTimeout);
    }
    
    // Définir un nouveau timeout pour réactiver les rotations après 0,4 secondes
    this.rotationCooldownTimeout = window.setTimeout(() => {
      this.rotationCooldown = false;
      console.log("Rotation cooldown désactivé");
      this.rotationCooldownTimeout = null;
    }, 400); // 400 ms = 0,4 secondes
  }

  private handleKeyDown(event: KeyboardEvent): void {
    // Skip if controls are disabled
    if (!this.controlsEnabled) return;
    
    // Mettre à jour l'état de la touche
    this.keyStates[event.key] = true;
    console.log("handleKeyDown", event.key);
    
    // Movement keys based on keyboard layout
    const forwardKeys = this.keyboardLayout === 'qwerty' ? ['ArrowUp', 'w'] : ['ArrowUp', 'z'];
    const backwardKeys = ['ArrowDown', 's']; // Same for both layouts
    const leftKeys = this.keyboardLayout === 'qwerty' ? ['ArrowLeft', 'a'] : ['ArrowLeft', 'q'];
    const rightKeys = ['ArrowRight', 'd']; // Same for both layouts
    
    if (forwardKeys.includes(event.key)) {
      this.isMoving = true;
      this.direction.z = 1;
      this.resetToThirdPersonView(); // Retourner à la vue 3ème personne sur mouvement
    } else if (backwardKeys.includes(event.key)) {
      this.isMoving = true;
      this.direction.z = -1;
      this.resetToThirdPersonView(); // Retourner à la vue 3ème personne sur mouvement
    } else if (leftKeys.includes(event.key) || rightKeys.includes(event.key)) {
      this.resetToThirdPersonView(); // Retourner à la vue 3ème personne sur mouvement
    } else if (event.key === 'Tab') {
      // Empêcher le comportement par défaut de Tab
      event.preventDefault();
      this.toggleTopDownView();
    } else if (event.key === 'e' || event.key === 'E') {
      // Démarrer la vente si on est dans la zone du marché noir
      if (this.isInBlackMarketArea) {
        console.log("Touche E pressée dans la zone du marché noir, démarrage de la vente");
        this.startSelling();
      } else {
        console.log("Touche E pressée mais pas dans la zone du marché noir");
      }
    } else if (event.key === 'k' || event.key === 'K') {
      // Toggle between QWERTY and AZERTY layout
      this.keyboardLayout = this.keyboardLayout === 'qwerty' ? 'azerty' : 'qwerty';
      console.log(`Keyboard layout switched to: ${this.keyboardLayout}`);
      // Save the preference for future use
      localStorage.setItem('keyboardLayout', this.keyboardLayout);
      
      // Show keyboard layout message
      this.showKeyboardLayoutMessage();
    }
    
    // Normaliser pour éviter un mouvement plus rapide en diagonale
    this.direction.normalize();
  }

  private handleKeyUp(event: KeyboardEvent): void {
    // Skip if controls are disabled
    if (!this.controlsEnabled) return;
    
    // Mettre à jour l'état de la touche
    this.keyStates[event.key] = false;
    
    // Movement keys based on keyboard layout
    const forwardKeys = this.keyboardLayout === 'qwerty' ? ['ArrowUp', 'w'] : ['ArrowUp', 'z'];
    const backwardKeys = ['ArrowDown', 's']; // Same for both layouts
    
    if (forwardKeys.includes(event.key) || backwardKeys.includes(event.key)) {
      this.direction.z = 0;
    } else if (event.key === 'e' || event.key === 'E') {
      // Arrêter la vente quand on relâche la touche
      if (this.isSelling) {
        console.log("Touche E relâchée, arrêt de la vente");
        this.stopSelling();
      }
    }
    
    if (this.direction.length() === 0) {
      this.isMoving = false;
    }
  }
  
  private handleClick(event: MouseEvent): void {
    // Skip if controls are disabled
    if (!this.controlsEnabled) return;
    
    // Ignorer les clics sur les boutons de contrôle mobile
    if (
      event.target === this.mobileButtons.forward ||
      event.target === this.mobileButtons.backward ||
      event.target === this.mobileButtons.left ||
      event.target === this.mobileButtons.right ||
      event.target === this.mobileButtons.map
    ) {
      return;
    }
    
  }

  // Méthode pour définir le callback de changement de vue
  public setOnCameraViewChanged(callback: (isTopDown: boolean) => void): void {
    this.onCameraViewChanged = callback;
  }

  // Méthode pour activer/désactiver la vue top-down
  public toggleTopDownView(): void {
    this.isTopDownView = !this.isTopDownView;
    if (this.onCameraViewChanged) {
      this.onCameraViewChanged(this.isTopDownView);
    }
  }

  // Méthode pour vérifier si on est en vue top-down
  public isInTopDownView(): boolean {
    return this.isTopDownView;
  }

  // Méthode pour réinitialiser la vue à la 3ème personne
  public resetToThirdPersonView(): void {
    if (this.isTopDownView) {
      this.isTopDownView = false;
      if (this.onCameraViewChanged) {
        this.onCameraViewChanged(false);
      }
    }
  }

  // Définit la position initiale du joueur
  public setInitialPosition(position: { x: number, y: number, z: number }): void {
    console.log("Initializing player position:", position);
    // S'assurer que la position est valide (pas NaN ou infini)
    if (isNaN(position.x) || isNaN(position.z) || 
        !isFinite(position.x) || !isFinite(position.z)) {
      console.error("Invalid position received:", position);
      return;
    }
    
    // Appliquer la position au vecteur de position
    this.position.set(position.x, position.y || 0.27, position.z);
    
    // Appliquer immédiatement à l'objet joueur
    this.player.position.copy(this.position);
    
    // Initialiser la vitesse actuelle à 0
    this.currentSpeed = 0;
    
    // Redéfinir la rotation pour s'assurer que le joueur est correctement orienté
    this.player.rotation.y = this.targetRotationY;
    
    // Create or update label for the current player if we have a label manager
    if (this.labelManager && this.socket.id && this.player.userData.name) {
      this.labelManager.createLabel(
        this.socket.id, 
        this.player.userData.name, 
        this.position
      );
    }
    
    console.log("Player position initialized to:", this.position);
  }

  // Enable controls when game starts
  public enableControls(): void {
    this.controlsEnabled = true;
  }

  public disableControls(): void {
    this.controlsEnabled = false;
    
    // Réinitialiser tous les états des touches pour éviter le mouvement après réactivation
    for (const key in this.keyStates) {
      this.keyStates[key] = false;
    }
    
    // Arrêter le mouvement
    this.isMoving = false;
    this.direction.set(0, 0, 0);
  }

  // Set the label manager from Game
  public setLabelManager(labelManager: LabelManager2D): void {
    this.labelManager = labelManager;
  }

  // Créer les éléments UI pour le marché noir
  private createBlackMarketUI(): void {
    console.log("Création des éléments UI pour le marché noir");
    
    // Check if mobile
    const isMobile = this.isMobile;
    
    // Créer le conteneur de la barre de progression
    this.sellProgressBarContainer = document.createElement('div');
    this.sellProgressBarContainer.id = 'sell-progress-container';
    this.sellProgressBarContainer.style.position = 'fixed';
    this.sellProgressBarContainer.style.bottom = isMobile ? '140px' : '160px';
    this.sellProgressBarContainer.style.left = '50%';
    this.sellProgressBarContainer.style.transform = 'translateX(-50%)';
    this.sellProgressBarContainer.style.width = isMobile ? '220px' : '300px';
    this.sellProgressBarContainer.style.height = isMobile ? '15px' : '20px';
    this.sellProgressBarContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    this.sellProgressBarContainer.style.borderRadius = '10px';
    this.sellProgressBarContainer.style.overflow = 'hidden';
    this.sellProgressBarContainer.style.display = 'none';
    this.sellProgressBarContainer.style.zIndex = '1001';
    
    // Créer la barre de progression
    this.sellProgressBar = document.createElement('div');
    this.sellProgressBar.id = 'sell-progress-bar';
    this.sellProgressBar.style.width = '0%';
    this.sellProgressBar.style.height = '100%';
    this.sellProgressBar.style.backgroundColor = '#FFD700'; // Couleur or pour l'argent
    this.sellProgressBar.style.transition = 'width 0.1s linear';
    
    // Ajouter la barre de progression au conteneur
    this.sellProgressBarContainer.appendChild(this.sellProgressBar);
    
    // Ajouter le conteneur au DOM
    document.body.appendChild(this.sellProgressBarContainer);
    console.log("Barre de progression du marché noir ajoutée au DOM avec l'ID:", this.sellProgressBarContainer.id);
    
    // Créer un bouton de vente pour tous les appareils (mobile et desktop)
    const sellButton = document.createElement('button');
    sellButton.id = 'sell-btn';
    
    // Texte différent selon la plateforme
    sellButton.innerHTML = this.isMobile ? 'Sell Stolen Package' : 'Long press E to sell your stolen packages';
    
    sellButton.style.position = 'fixed';
    sellButton.style.bottom = '50px';
    sellButton.style.left = '50%';
    sellButton.style.transform = 'translateX(-50%)';
    sellButton.style.padding = '15px 30px';
    sellButton.style.backgroundColor = '#FFD700';
    sellButton.style.color = 'black';
    sellButton.style.border = 'none';
    sellButton.style.borderRadius = '25px';
    sellButton.style.fontFamily = "'Nunito', sans-serif";
    sellButton.style.fontSize = '18px';
    sellButton.style.fontWeight = 'bold';
    sellButton.style.cursor = 'pointer';
    sellButton.style.display = 'none';
    sellButton.style.zIndex = '1002';
    
    // Ajouter les gestionnaires d'événements pour le bouton de vente
    sellButton.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (!this.controlsEnabled) return;
      this.startSelling();
    });
    
    sellButton.addEventListener('touchend', () => {
      this.stopSelling();
    });
    
    sellButton.addEventListener('mousedown', () => {
      if (!this.controlsEnabled) return;
      this.startSelling();
    });
    
    sellButton.addEventListener('mouseup', () => {
      this.stopSelling();
    });
    
    // Ajouter le bouton au DOM
    document.body.appendChild(sellButton);
    console.log(`Bouton de vente ajouté au DOM avec l'ID: ${sellButton.id}`);
    
    // Garder une référence au bouton
    this.mobileButtons.sell = sellButton;
    
    console.log("Création des éléments UI pour le marché noir terminée");
  }
  
  // Vérifie si le joueur est dans la zone du marché noir
  private checkBlackMarketArea(): void {
    if (!this.isGridInitialized) {
      // console.log("checkBlackMarketArea: La grille n'est pas encore initialisée");
      return;
    }
    
    // Récupérer les indices de la grille pour la position actuelle
    const indices = this.cityGenerator.calculateIndex(this.position);
    
    // Récupérer les données de la grille
    const cityGrid = this.cityGenerator.getCityGrid();
    if (!cityGrid || !cityGrid.blocks) {
      // console.log("checkBlackMarketArea: Pas de données de grille disponibles");
      return;
    }
    
    // Vérifier si la position est dans les limites de la grille
    if (indices.i < 0 || indices.i >= cityGrid.blocks.length || 
        indices.j < 0 || indices.j >= cityGrid.blocks[0].length) {
      // console.log("checkBlackMarketArea: Position hors limites de la grille:", indices);
      return;
    }
    
    // Vérifier si on est sur une cellule de marché noir (type 12)
    const cellType = cityGrid.blocks[indices.i][indices.j];
    const wasInBlackMarketArea = this.isInBlackMarketArea;
    this.isInBlackMarketArea = cellType === 12;
    
    // console.log("checkBlackMarketArea - Position:", indices.i, indices.j, "Type:", cellType, "Est dans la zone:", this.isInBlackMarketArea, "Était dans la zone:", wasInBlackMarketArea);
    
    // Si on vient d'entrer dans la zone du marché noir
    if (!wasInBlackMarketArea && this.isInBlackMarketArea) {
      console.log("Le joueur vient d'entrer dans la zone du marché noir");
      this.showBlackMarketNotification();
    } 
    // Si on vient de quitter la zone du marché noir
    else if (wasInBlackMarketArea && !this.isInBlackMarketArea) {
      console.log("Le joueur vient de quitter la zone du marché noir");
      this.hideBlackMarketNotification();
    }
  }
  
  // Affiche la notification du marché noir
  private showBlackMarketNotification(): void {
    console.log("showBlackMarketNotification appelée");
    
    // Vérifier si le joueur a des colis
    this.socket.emit('checkPackages', (hasPackages: boolean) => {
      console.log("Résultat de checkPackages:", hasPackages);
      
      if (hasPackages) {
        console.log("Le joueur a des colis, affichage du bouton de vente");
        
        // Afficher le bouton de vente pour tous les appareils
        if (this.mobileButtons.sell) {
          this.mobileButtons.sell.style.display = 'block';
        } else {
          console.error("Le bouton de vente est null!");
        }
      } else {
        console.log("Le joueur n'a pas de colis, pas de bouton de vente");
      }
    });
  }
  
  // Cache la notification du marché noir
  private hideBlackMarketNotification(): void {
    // Cacher le bouton de vente
    if (this.mobileButtons.sell) {
      this.mobileButtons.sell.style.display = 'none';
    }
    
    // Arrêter la vente en cours si nécessaire
    this.stopSelling();
  }
  
  // Démarre la vente d'un colis
  private startSelling(): void {
    if (!this.isInBlackMarketArea || this.isSelling) {
      console.log("Impossible de démarrer la vente: déjà en cours ou pas dans la zone du marché noir");
      return;
    }
    
    console.log("Démarrage de la vente d'un colis au marché noir");
    this.isSelling = true;
    this.sellStartTime = performance.now();
    
    // Afficher la barre de progression
    if (this.sellProgressBarContainer) {
      this.sellProgressBarContainer.style.display = 'block';
    }
    
    // Réinitialiser la barre de progression
    if (this.sellProgressBar) {
      this.sellProgressBar.style.width = '0%';
    }
    
    // Animer la barre de progression
    this.updateSellProgressBar();
    
    // Démarrer le timer pour la vente
    if (this.sellTimeout !== null) {
      window.clearTimeout(this.sellTimeout);
    }
    
    this.sellTimeout = window.setTimeout(() => {
      this.completeSale();
    }, this.sellDuration);
  }
  
  // Arrête la vente d'un colis
  private stopSelling(): void {
    if (!this.isSelling) {
      console.log("Aucune vente en cours à arrêter");
      return;
    }
    
    console.log("Arrêt de la vente d'un colis au marché noir");
    this.isSelling = false;
    
    // Cacher la barre de progression
    if (this.sellProgressBarContainer) {
      this.sellProgressBarContainer.style.display = 'none';
    }
    
    // Annuler le timer de vente
    if (this.sellTimeout !== null) {
      window.clearTimeout(this.sellTimeout);
      this.sellTimeout = null;
    }
  }
  
  // Met à jour la barre de progression de la vente
  private updateSellProgressBar(): void {
    if (!this.isSelling) return;
    
    const currentTime = performance.now();
    const elapsed = currentTime - this.sellStartTime;
    const progress = Math.min(elapsed / this.sellDuration * 100, 100);
    
    // Mettre à jour la barre de progression
    if (this.sellProgressBar) {
      this.sellProgressBar.style.width = `${progress}%`;
    }
    
    // Continuer l'animation si la vente est en cours
    if (progress < 100 && this.isSelling) {
      requestAnimationFrame(() => this.updateSellProgressBar());
    }
  }
  
  // Finalise la vente d'un colis
  private completeSale(): void {
    if (!this.isSelling) {
      console.log("Aucune vente en cours à finaliser");
      return;
    }
    
    console.log("Finalisation de la vente d'un colis au marché noir");
    
    // Informer le serveur de la vente
    this.socket.emit('sellPackageToBlackMarket', (result: { success: boolean, value: number }) => {
      console.log("Résultat de la vente:", result);
      
      if (result.success) {
        // Afficher une notification de vente réussie
        this.showSaleNotification(result.value);
        
        // Vérifier s'il reste des colis à vendre
        this.socket.emit('checkPackages', (hasPackages: boolean) => {
          console.log("Vérification des colis restants:", hasPackages);
          
          if (hasPackages) {
            // Redémarrer le processus de vente pour le prochain colis
            this.stopSelling();
            this.startSelling();
          } else {
            // Arrêter le processus de vente
            this.stopSelling();
            this.hideBlackMarketNotification();
          }
        });
      } else {
        // Arrêter le processus de vente en cas d'échec
        this.stopSelling();
      }
    });
  }
  
  // Affiche une notification de vente réussie
  private showSaleNotification(value: number): void {
    console.log("Affichage de la notification de vente réussie:", value);
    
    const notification = document.createElement('div');
    notification.style.position = 'fixed';
    notification.style.top = '30%';
    notification.style.left = '50%';
    notification.style.transform = 'translate(-50%, -50%)';
    notification.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    notification.style.color = '#FFD700'; // Couleur or
    notification.style.padding = '15px 25px';
    notification.style.borderRadius = '8px';
    notification.style.fontFamily = "'Nunito', sans-serif";
    notification.style.fontSize = '22px';
    notification.style.fontWeight = 'bold';
    notification.style.textAlign = 'center';
    notification.style.zIndex = '1500';
    notification.style.opacity = '0';
    notification.style.transition = 'opacity 0.3s ease-in-out';
    notification.textContent = `Package sold: +${value} $`;
    
    // Ajouter au DOM
    document.body.appendChild(notification);
    console.log("Notification de vente ajoutée au DOM");
    
    // Animation d'apparition
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 10);
    
    // Disparition après 2 secondes
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
          console.log("Notification de vente supprimée du DOM");
        }
      }, 300);
    }, 2000);
  }

  // Gérer la collision avec un patrouilleur
  private handlePatrolCollision(data: any): void {
    if (this.isBeingInspected) {
      return; // Déjà en cours d'inspection
    }
    
    // Vérifier si une collision avec une patrouille s'est produite dans les 3 dernières secondes
    const currentTime = Date.now();
    if (currentTime - this.lastPatrolCollisionTime < this.PATROL_COLLISION_COOLDOWN) {
      console.log("Patrol collision ignored: cooldown period active");
      return; // Ignorer cette collision pendant la période de cooldown
    }
    
    console.log("Collision with police patrol detected");
    
    // Mettre à jour le timestamp de la dernière collision
    this.lastPatrolCollisionTime = currentTime;
    
    // Vérifier si le joueur a des colis volés ou est un voleur
    this.socket.emit('checkStolenPackages', (hasStolenPackages: boolean) => {
      this.hasStolenPackages = hasStolenPackages;
      
      // Vérifier si le joueur est un voleur, a des colis volés, ou a des colis manquants
      if (hasStolenPackages || this.thief || this.hasMissingPackets()) {
        // Le joueur est immobilisé pendant 5 secondes
        this.handlePoliceImmobilization();
        
        // Afficher un message spécifique si c'est à cause des colis manquants
        if (!hasStolenPackages && !this.thief && this.hasMissingPackets()) {
          this.showNotification('You are responsible for lost packages!', '#e74c3c', 3000);
        }
      } else {
        // Le joueur est clean, rien ne se passe mais on augmente sa confiance
        console.log("Player is not a thief and has no missing packets, inspection passed");
        
        // Informer le serveur que l'inspection s'est bien passée
        this.socket.emit('inspectionPassed');
        
        // Afficher une notification positive
        this.showNotification('Inspection OK. have a good day', '#2ecc71', 1000);
      }
    });
  }
  
  // Gérer l'immobilisation par la police (5 secondes)
  private handlePoliceImmobilization(): void {
    console.log("Player immobilized by police for 5 seconds");
    
    // Marquer que le joueur est en cours d'inspection pour éviter les collisions multiples
    this.isBeingInspected = true;
    
    // Afficher une notification d'immobilisation
    this.showPoliceImmobilizationNotification();
    
    // Arrêter le mouvement du joueur
    this.disableControls();
    
    // Informer le serveur de l'immobilisation
    this.socket.emit('playerImmobilized');
    
    // Réactiver les contrôles après 5 secondes
    if (this.inspectionTimeout !== null) {
      window.clearTimeout(this.inspectionTimeout);
    }
    
    this.inspectionTimeout = window.setTimeout(() => {
      this.isBeingInspected = false;
      this.enableControls();
      this.hidePoliceImmobilizationNotification();
    }, 5000);
  }
  
  // Afficher une notification d'immobilisation
  private showPoliceImmobilizationNotification(): void {
    const notification = document.createElement('div');
    notification.id = 'police-immobilization-notification';
    notification.style.position = 'fixed';
    notification.style.top = '30%';
    notification.style.left = '50%';
    notification.style.transform = 'translate(-50%, -50%)';
    notification.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    notification.style.color = '#e74c3c'; // Rouge pour l'immobilisation
    notification.style.padding = '20px 30px';
    notification.style.borderRadius = '8px';
    notification.style.fontFamily = "'Nunito', sans-serif";
    notification.style.fontSize = '24px';
    notification.style.fontWeight = 'bold';
    notification.style.textAlign = 'center';
    notification.style.zIndex = '1500';
    notification.style.opacity = '0'; // Start invisible for fade in
    notification.style.transition = 'opacity 0.3s ease-in-out'; // Add transition for fade effect
    notification.textContent = 'Fine for stolen package: 50%';
    
    // Ajouter au DOM
    document.body.appendChild(notification);
    
    // Fade in
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 10);
    
    // Fade out and remove after 1 second
    setTimeout(() => {
      notification.style.opacity = '0';
      // Remove from DOM after fade out animation completes
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 1000);
  }
  
  // Cacher la notification d'immobilisation
  private hidePoliceImmobilizationNotification(): void {
    // No need to manually remove the notification anymore as it's automatically 
    // removed after the fade out animation. This method is kept for compatibility.
    const notification = document.getElementById('police-immobilization-notification');
    if (notification && notification.parentNode) {
      notification.style.opacity = '0';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }
  }

  // Gérer la fin de partie
  private handleGameOver(data: any): void {
    console.log("Game over:", data);
    
    // Désactiver les contrôles
    this.disableControls();
    
    // Afficher l'écran de fin de partie
    this.showGameOverScreen(data.reason, data.score);
    
    // Appeler le callback de fin de partie si défini
    if (this.gameOverCallback) {
      this.gameOverCallback(data.reason, data.score);
    }
  }
  
  // Afficher l'écran de fin de partie
  private showGameOverScreen(reason: string, score: number): void {
    // Créer l'écran de fin de partie
    const gameOverScreen = document.createElement('div');
    gameOverScreen.id = 'game-over-screen';
    gameOverScreen.style.position = 'fixed';
    gameOverScreen.style.top = '0';
    gameOverScreen.style.left = '0';
    gameOverScreen.style.width = '100%';
    gameOverScreen.style.height = '100%';
    gameOverScreen.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    gameOverScreen.style.display = 'flex';
    gameOverScreen.style.flexDirection = 'column';
    gameOverScreen.style.justifyContent = 'center';
    gameOverScreen.style.alignItems = 'center';
    gameOverScreen.style.zIndex = '2000';
    
    // Titre
    const title = document.createElement('h1');
    title.style.color = '#e74c3c';
    title.style.fontFamily = "'Nunito', sans-serif";
    title.style.fontSize = '48px';
    title.style.marginBottom = '20px';
    title.textContent = 'GAME OVER';
    
    // Raison
    const reasonText = document.createElement('p');
    reasonText.style.color = '#ffffff';
    reasonText.style.fontFamily = "'Nunito', sans-serif";
    reasonText.style.fontSize = '24px';
    reasonText.style.marginBottom = '20px';
    
    if (reason === 'arrested') {
      reasonText.textContent = 'You have been arrested by the police';
    } else {
      reasonText.textContent = 'Game over';
    }
    
    // Score
    const scoreText = document.createElement('p');
    scoreText.style.color = '#ffffff';
    scoreText.style.fontFamily = "'Nunito', sans-serif";
    scoreText.style.fontSize = '24px';
    scoreText.style.marginBottom = '40px';
    scoreText.textContent = `Score final: ${score}`;
    
    // Bouton pour recommencer
    const restartButton = document.createElement('button');
    restartButton.style.padding = '15px 30px';
    restartButton.style.backgroundColor = '#3498db';
    restartButton.style.color = 'white';
    restartButton.style.border = 'none';
    restartButton.style.borderRadius = '5px';
    restartButton.style.fontFamily = "'Nunito', sans-serif";
    restartButton.style.fontSize = '18px';
    restartButton.style.cursor = 'pointer';
    restartButton.textContent = 'Recommencer';
    
    // Ajouter l'événement de clic pour recommencer
    restartButton.addEventListener('click', () => {
      // Supprimer l'écran de fin de partie
      if (gameOverScreen.parentNode) {
        gameOverScreen.parentNode.removeChild(gameOverScreen);
      }
      
      // Recharger la page pour recommencer
      window.location.reload();
    });
    
    // Ajouter les éléments à l'écran de fin de partie
    gameOverScreen.appendChild(title);
    gameOverScreen.appendChild(reasonText);
    gameOverScreen.appendChild(scoreText);
    gameOverScreen.appendChild(restartButton);
    
    // Ajouter l'écran de fin de partie au DOM
    document.body.appendChild(gameOverScreen);
  }
  
  // Définir le callback de fin de partie
  public setGameOverCallback(callback: (reason: string, score: number) => void): void {
    this.gameOverCallback = callback;
  }

  // Detect keyboard layout (QWERTY vs AZERTY)
  private detectKeyboardLayout(): void {
    // Try to determine keyboard layout based on navigator language
    const lang = navigator.language || (navigator as any).userLanguage;
    if (lang) {
      const langLower = lang.toLowerCase();
      // French, Belgian, and some other countries typically use AZERTY
      if (langLower.startsWith('fr') || langLower.startsWith('be')) {
        this.keyboardLayout = 'azerty';
        console.log('AZERTY keyboard layout detected based on language');
      } else {
        this.keyboardLayout = 'qwerty';
        console.log('QWERTY keyboard layout detected based on language');
      }
    }
    
    // Let user override with localStorage if they've set it before
    const savedLayout = localStorage.getItem('keyboardLayout');
    if (savedLayout === 'azerty' || savedLayout === 'qwerty') {
      this.keyboardLayout = savedLayout;
      console.log(`Using saved keyboard layout: ${savedLayout}`);
    }
  }

  // Show keyboard layout message
  private showKeyboardLayoutMessage(): void {
    const layoutName = this.keyboardLayout.toUpperCase();
    const keysText = this.keyboardLayout === 'qwerty' ? 'WASD' : 'ZQSD';
    
    const notification = document.createElement('div');
    notification.style.position = 'fixed';
    notification.style.top = '20%';
    notification.style.left = '50%';
    notification.style.transform = 'translateX(-50%)';
    notification.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    notification.style.color = 'white';
    notification.style.padding = '15px 20px';
    notification.style.borderRadius = '8px';
    notification.style.fontFamily = "'Nunito', sans-serif";
    notification.style.fontSize = '18px';
    notification.style.fontWeight = 'bold';
    notification.style.textAlign = 'center';
    notification.style.zIndex = '1500';
    notification.style.opacity = '0';
    notification.style.transition = 'opacity 0.3s ease-in-out';
    notification.innerHTML = `${layoutName} Keyboard Layout<br>Movement: ${keysText}<br>Press K to change`;
    
    // Ajouter au DOM
    document.body.appendChild(notification);
    
    // Animation d'apparition
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 10);
    
    // Disparition après 3 secondes
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  // Nouvelle méthode pour vérifier si le joueur est dans la zone de la police station
  private checkPoliceStationArea(): void {
    // Récupérer les indices de la grille pour la position actuelle
    const indices = this.cityGenerator.calculateIndex(this.position);
    const cityGrid = this.cityGenerator.getCityGrid();
    
    // Vérifier si la position est dans la grille
    const isInGrid = cityGrid && cityGrid.blocks && 
                   indices.i >= 0 && indices.i < cityGrid.blocks.length &&
                   indices.j >= 0 && indices.j < cityGrid.blocks[0].length;
    
    // Vérifier si la position actuelle est sur une case de police station (type 20 ou 21)
    const wasInPoliceStation = this.isInPoliceStationArea;
    
    if (isInGrid) {
      const cellType = cityGrid.blocks[indices.i][indices.j];
      this.isInPoliceStationArea = cellType === 20 || cellType === 21;
    } else {
      this.isInPoliceStationArea = false;
    }
    
    // Si on vient d'entrer dans la zone de la police station
    if (!wasInPoliceStation && this.isInPoliceStationArea) {
      console.log("Le joueur vient d'entrer dans la zone de la police station");
      this.handlePoliceStationEntry();
    }
    
    // Si on vient de quitter la zone de la police station
    if (wasInPoliceStation && !this.isInPoliceStationArea) {
      console.log("Le joueur vient de quitter la zone de la police station");
      this.handlePoliceStationExit();
    }
  }
  
  // Gérer l'entrée dans la police station
  private handlePoliceStationEntry(): void {
    // Si le joueur a des colis volés, il est toujours arrêté dans la police station
    // Cette règle reste inchangée
    if (this.hasStolenPackages) {
      // Dans ce cas spécifique, on utilise encore l'ancienne fonction d'arrestation
      // car le joueur qui entre dans une police station avec des colis volés 
      // est toujours officiellement arrêté (fin de partie)
      this.handleArrest();
      return;
    }
    
    // Si le joueur est un voleur mais n'a pas de colis volés,
    // il peut déclarer le vol et réinitialiser son statut
    if (this.thief) {
      // Envoyer l'événement de déclaration de vol au serveur
      this.socket.emit('declareTheft');
      this.thief = false; // Réinitialiser le statut localement
      this.showNotification('Vol déclaré à la police', '#4CAF50', 3000);
    }
    
    // Créer la notification pour indiquer que le joueur est dans la police station
    this.showPoliceStationNotification();
  }
  
  // Gérer la sortie de la police station
  private handlePoliceStationExit(): void {
    // Cacher la notification de police station si elle existe
    if (this.policeStationNotification && this.policeStationNotification.parentNode) {
      this.policeStationNotification.parentNode.removeChild(this.policeStationNotification);
      this.policeStationNotification = null;
    }
    
    // Si une déclaration de vol est en cours, l'annuler
    if (this.isDeclaring) {
      console.log("Déclaration annulée: sortie de la zone de police");
      this.stopDeclaring();
    }
  }
  
  // Afficher une notification pour la police station
  private showPoliceStationNotification(): void {
    // Supprimer toute notification existante
    if (this.policeStationNotification && this.policeStationNotification.parentNode) {
      this.policeStationNotification.parentNode.removeChild(this.policeStationNotification);
    }
    
    // Créer un nouvel élément de notification
    const notification = document.createElement('div');
    notification.id = 'police-station-notification';
    notification.style.position = 'fixed';
    notification.style.bottom = '50px';
    notification.style.left = '50%';
    notification.style.transform = 'translateX(-50%)';
    notification.style.backgroundColor = '#3498db'; // Bleu pour la police
    notification.style.color = 'white';
    notification.style.padding = '15px 20px';
    notification.style.borderRadius = '5px';
    notification.style.fontFamily = "'Arial', sans-serif";
    notification.style.fontSize = '16px';
    notification.style.fontWeight = 'bold';
    notification.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    notification.style.zIndex = '1000';
    notification.style.textAlign = 'center';
    
    // Texte adapté selon le statut du joueur
    if (this.thief) {
      notification.textContent = 'Press E to report a theft';
      
      // Ajouter un gestionnaire d'événements pour la touche E
      const declareTheftHandler = (event: KeyboardEvent) => {
        if (event.key === 'e' || event.key === 'E') {
          if (this.isInPoliceStationArea && this.thief && !this.isDeclaring) {
            // Commencer le processus de déclaration avec barre de progression
            this.startDeclaring();
            
            // Supprimer ce gestionnaire d'événements
            document.removeEventListener('keydown', declareTheftHandler);
          }
        }
      };
      
      document.addEventListener('keydown', declareTheftHandler);
    } else {
      notification.textContent = 'Police Station';
    }
    
    document.body.appendChild(notification);
    this.policeStationNotification = notification;
  }
  
  // Méthode générique pour afficher une notification temporaire
  private showNotification(message: string, color: string, duration: number): void {
    const notification = document.createElement('div');
    notification.style.position = 'fixed';
    notification.style.top = '50%';
    notification.style.left = '50%';
    notification.style.transform = 'translate(-50%, -50%)';
    notification.style.backgroundColor = color;
    notification.style.color = 'white';
    notification.style.padding = '15px 20px';
    notification.style.borderRadius = '5px';
    notification.style.fontFamily = "'Arial', sans-serif";
    notification.style.fontSize = '18px';
    notification.style.fontWeight = 'bold';
    notification.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    notification.style.zIndex = '1000';
    notification.style.textAlign = 'center';
    notification.style.opacity = '0'; // Start with opacity 0
    notification.style.transition = 'opacity 0.3s ease-in-out'; // Add transition for fade effect
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    // Fade in (small delay to ensure transition works)
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 10);
    
    // Fade out and then remove
    setTimeout(() => {
      notification.style.opacity = '0';
      // Wait for fade out animation to complete before removing
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300); // Duration of the fade out transition
    }, duration);
  }

  
  // Gérer l'arrestation du joueur (méthode maintenue pour compatibilité)
  private handleArrest(): void {
    // Méthode maintenue pour la compatibilité avec le code existant
    console.log("Arrest method called (used by Police Station)");
    
    // Si une déclaration de vol est en cours, l'annuler
    if (this.isDeclaring) {
      this.stopDeclaring();
    }
    
    // Afficher une notification d'arrestation
    this.showArrestNotification();
    
    // Arrêter le mouvement du joueur
    this.disableControls();
    
    // Informer le serveur de l'arrestation
    this.socket.emit('playerArrested');
  }
  
  // Afficher une notification d'arrestation (pour compatibilité)
  private showArrestNotification(): void {
    const notification = document.createElement('div');
    notification.id = 'police-arrest-notification';
    notification.style.position = 'fixed';
    notification.style.top = '30%';
    notification.style.left = '50%';
    notification.style.transform = 'translate(-50%, -50%)';
    notification.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    notification.style.color = '#e74c3c'; // Rouge pour l'arrestation
    notification.style.padding = '20px 30px';
    notification.style.borderRadius = '8px';
    notification.style.fontFamily = "'Nunito', sans-serif";
    notification.style.fontSize = '24px';
    notification.style.fontWeight = 'bold';
    notification.style.textAlign = 'center';
    notification.style.zIndex = '1500';
    notification.textContent = 'ARRESTED BY POLICE';
    
    // Ajouter au DOM
    document.body.appendChild(notification);
  }

  // Créer l'interface utilisateur pour la déclaration de vol
  private createDeclareUI(): void {
    // Check if mobile
    const isMobile = this.isMobile;
    
    // Créer le conteneur pour la barre de progression
    const container = document.createElement('div');
    container.id = 'declare-progress-container';
    container.style.position = 'fixed';
    container.style.top = '50%';
    container.style.left = '50%';
    container.style.transform = 'translate(-50%, -50%)';
    container.style.width = isMobile ? '250px' : '300px';
    container.style.height = isMobile ? '25px' : '30px';
    container.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    container.style.borderRadius = '15px';
    container.style.overflow = 'hidden';
    container.style.display = 'none';
    container.style.zIndex = '1000';
    
    // Créer la barre de progression
    const progressBar = document.createElement('div');
    progressBar.id = 'declare-progress';
    progressBar.style.width = '0%';
    progressBar.style.height = '100%';
    progressBar.style.backgroundColor = '#3498db'; // Bleu pour la police
    progressBar.style.transition = 'width 0.1s linear';
    
    // Ajouter la barre de progression au conteneur
    container.appendChild(progressBar);
    
    // Texte de déclaration au milieu de la barre
    const text = document.createElement('div');
    text.textContent = 'Declaring Theft...';
    text.style.position = 'absolute';
    text.style.top = '50%';
    text.style.left = '50%';
    text.style.transform = 'translate(-50%, -50%)';
    text.style.color = 'white';
    text.style.fontFamily = "'Arial', sans-serif";
    text.style.fontSize = isMobile ? '12px' : '14px';
    text.style.fontWeight = 'bold';
    text.style.textShadow = '1px 1px 2px rgba(0, 0, 0, 0.8)';
    
    // Ajouter le texte au conteneur
    container.appendChild(text);
    
    // Ajouter le conteneur au document
    document.body.appendChild(container);
    
    // Stocker les références
    this.declareProgressBarContainer = container;
    this.declareProgressBar = progressBar;
  }

  // Démarre la déclaration de vol
  private startDeclaring(): void {
    if (!this.isInPoliceStationArea || this.isDeclaring) {
      console.log("Impossible de démarrer la déclaration: déjà en cours ou pas dans la zone de police");
      return;
    }
    
    console.log("Démarrage de la déclaration de vol à la police");
    this.isDeclaring = true;
    this.declareStartTime = performance.now();
    
    // Afficher la barre de progression
    if (this.declareProgressBarContainer) {
      this.declareProgressBarContainer.style.display = 'block';
    }
    
    // Réinitialiser la barre de progression
    if (this.declareProgressBar) {
      this.declareProgressBar.style.width = '0%';
    }
    
    // Animer la barre de progression
    this.updateDeclareProgressBar();
    
    // Démarrer le timer pour la déclaration
    if (this.declareTimeout !== null) {
      window.clearTimeout(this.declareTimeout);
    }
    
    this.declareTimeout = window.setTimeout(() => {
      this.completeDeclaration();
    }, this.declareDuration);
  }
  
  // Arrête la déclaration de vol
  private stopDeclaring(): void {
    if (!this.isDeclaring) {
      console.log("Aucune déclaration en cours à arrêter");
      return;
    }
    
    console.log("Arrêt de la déclaration de vol à la police");
    this.isDeclaring = false;
    
    // Cacher la barre de progression
    if (this.declareProgressBarContainer) {
      this.declareProgressBarContainer.style.display = 'none';
    }
    
    // Annuler le timer de déclaration
    if (this.declareTimeout !== null) {
      window.clearTimeout(this.declareTimeout);
      this.declareTimeout = null;
    }
  }
  
  // Met à jour la barre de progression de la déclaration
  private updateDeclareProgressBar(): void {
    if (!this.isDeclaring) return;
    
    const currentTime = performance.now();
    const elapsed = currentTime - this.declareStartTime;
    const progress = Math.min(elapsed / this.declareDuration * 100, 100);
    
    // Mettre à jour la barre de progression
    if (this.declareProgressBar) {
      this.declareProgressBar.style.width = `${progress}%`;
    }
    
    // Continuer l'animation si la déclaration est en cours
    if (progress < 100 && this.isDeclaring) {
      requestAnimationFrame(() => this.updateDeclareProgressBar());
    }
  }
  
  // Finalise la déclaration de vol
  private completeDeclaration(): void {
    if (!this.isDeclaring) {
      console.log("Aucune déclaration en cours à finaliser");
      return;
    }
    
    console.log("Finalisation de la déclaration de vol à la police");
    
    // Informer le serveur de la déclaration
    this.socket.emit('declareTheft');
    
    // Mettre à jour le statut localement
    this.thief = false;
    
    // Afficher une notification de déclaration réussie
    this.showDeclarationNotification();
    
    // Arrêter le processus de déclaration
    this.stopDeclaring();
  }
  
  // Affiche une notification de déclaration réussie
  private showDeclarationNotification(): void {
    console.log("Affichage de la notification de déclaration réussie");
    
    const notification = document.createElement('div');
    notification.style.position = 'fixed';
    notification.style.top = '30%';
    notification.style.left = '50%';
    notification.style.transform = 'translate(-50%, -50%)';
    notification.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    notification.style.color = '#3498db'; // Bleu pour la police
    notification.style.padding = '15px 25px';
    notification.style.borderRadius = '8px';
    notification.style.fontFamily = "'Nunito', sans-serif";
    notification.style.fontSize = '22px';
    notification.style.fontWeight = 'bold';
    notification.style.textAlign = 'center';
    notification.style.zIndex = '1500';
    notification.style.opacity = '0';
    notification.style.transition = 'opacity 0.3s ease-in-out';
    notification.textContent = 'Theft reported successfully';
    
    // Ajouter au DOM
    document.body.appendChild(notification);
    console.log("Notification de déclaration ajoutée au DOM");
    
    // Animation d'apparition
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 10);
    
    // Disparition après 2 secondes
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
          console.log("Notification de déclaration supprimée du DOM");
        }
      }, 300);
    }, 2000);
  }

  // Méthode pour vérifier si le joueur a des colis manquants
  public hasMissingPackets(): boolean {
    return this.missingPackets.length > 0;
  }

  // Méthode pour récupérer la liste des colis manquants
  public getMissingPackets(): string[] {
    return this.missingPackets;
  }
} 